import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import InstallPrompt from "@/components/InstallPrompt";

const BACKEND_URL = "";

/**
 * FWAA 2026 — Live Triage Event Page.
 * Blueprint v2.5.0 · Hurst Conference Center · Aug 6, 2026.
 *
 * Cyber-Tech UI:
 *   Canvas          #020A1F  Deep Cyber-Blue
 *   Borders         #A0A0A0  Metallic Silver / Brushed Chrome
 *   Highlights      #00FFFF  Bright Cyan
 *   Text            #FFFFFF  Pure White
 *   Interaction     Cyan border-glow on hover/click
 *
 * QR flow: purse holder scans booth QR → this page loads → they enter
 * address + market rent → split-screen renders (variation LEFT · CapEx
 * ledger RIGHT) with S = C × 1.20 target rent.
 */

const BG = "#020A1F";
const SILVER = "#A0A0A0";
const CYAN = "#00FFFF";
const WHITE = "#FFFFFF";
const MUTED = "#7a8ba1";

const injectFonts = () => {
  const ID = "fwaa-fonts";
  if (document.getElementById(ID)) return;
  const l = document.createElement("link");
  l.id = ID; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Inter:wght@300;400;500;600;700;800&display=swap";
  document.head.appendChild(l);
};
const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const FWAA = () => {
  const [address, setAddress] = useState("");
  const [unitConfig, setUnitConfig] = useState("2br_1ba");
  const [marketRent, setMarketRent] = useState(1450);
  const [portfolio, setPortfolio] = useState(1);
  const [triage, setTriage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanCount, setScanCount] = useState(null);

  // Identity gate state (Step 1 — captured BEFORE triage/apply/press etc.)
  const [affiliation, setAffiliation] = useState("");   // key
  const [affiliationLabel, setAffiliationLabel] = useState("");
  const [attendeeId, setAttendeeId] = useState("");
  const [nextRoute, setNextRoute] = useState("");       // returned by API
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateError, setGateError] = useState("");

  const AFFILIATIONS = [
    { key: "owner_gp",         label: "Owner / General Partner",      hint: "Portfolio owner or GP." },
    { key: "property_manager", label: "Property Manager",             hint: "On-site or regional PM." },
    { key: "asset_manager",    label: "Asset / Portfolio Manager",    hint: "Institutional asset side." },
    { key: "investor_lp",      label: "Investor / LP / Capital",      hint: "Passive capital or LP." },
    { key: "broker",           label: "Broker / Advisor",             hint: "Sales, debt, or advisory." },
    { key: "vendor_trade",     label: "Vendor / Trade / 1099",        hint: "Contractor, 1099, crew, cleaner, subcontractor." },
    { key: "ada_specialist",   label: "ADA / Section 504 Specialist", hint: "Accessibility / compliance." },
    { key: "media",            label: "Media / Press",                hint: "Editor, reporter, publisher." },
    { key: "sponsor",          label: "Sponsor / Partner",            hint: "Event sponsor or partner." },
    { key: "association",      label: "Association / FWA Staff",     hint: "FWA / industry organization." },
    { key: "other",            label: "Other",                         hint: "None of the above." },
  ];

  // Common trade / role titles for the JOB_TITLE datalist suggestions.
  // Free text still works — this just gives attendees a quick tap.
  const JOB_TITLE_SUGGESTIONS = [
    "1099 Worker", "Independent Contractor", "Subcontractor",
    "Cleaning Crew", "Turn Cleaner", "Punch-Out Technician",
    "Painter", "Flooring Installer", "HVAC Tech", "Electrician",
    "Plumber", "Handyman", "Cabinet Refacer", "Appliance Tech",
    "Property Manager", "Regional Property Manager",
    "Asset Manager", "Portfolio Manager", "Acquisitions Analyst",
    "General Partner", "Managing Partner", "Owner", "Principal",
    "Broker", "Investment Sales Broker", "Debt Advisor",
    "ADA Compliance Officer", "Section 504 Coordinator",
    "Reporter", "Editor", "Publisher", "Sponsor Representative",
    "FWA Staff", "Association Director",
  ];

  useEffect(() => {
    injectFonts();
    // Pre-populate from URL params if the QR sticker carried them
    const p = new URLSearchParams(window.location.search);
    if (p.get("address")) setAddress(p.get("address"));
    if (p.get("unit_config")) setUnitConfig(p.get("unit_config"));
    if (p.get("market_rent")) setMarketRent(Number(p.get("market_rent")) || 1450);
    if (p.get("portfolio_size")) setPortfolio(Number(p.get("portfolio_size")) || 1);
    // Allow deep-link bypass of the identity gate (CEO testing).
    if (p.get("skip_gate") === "1") {
      setAffiliation("owner_gp");
      setAffiliationLabel("Owner / General Partner");
      setNextRoute("triage");
      setAttendeeId("bypass");
    }
    // Live scan counter
    fetch(`${BACKEND_URL}/api/public/fwaa/status`)
      .then((r) => r.ok ? r.json() : null).then((d) => setScanCount(d?.total_triage_scans ?? 0)).catch(() => {});
  }, []);

  const submitIdentity = async () => {
    setGateError("");
    if (!affiliation) { setGateError("Pick the seat that fits you best."); return; }
    if (!fullName.trim()) { setGateError("Full name is required."); return; }
    setGateSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/fwaa/attendee`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          affiliation, full_name: fullName, company, job_title: jobTitle,
          email, phone, notes,
        }),
      });
      if (!r.ok) throw new Error("capture failed");
      const d = await r.json();
      setAttendeeId(d.attendee_id);
      setAffiliationLabel(d.affiliation_label || affiliation);
      setNextRoute(d.next_route || "walkthrough_request");
    } catch (e) {
      setGateError("Could not save. Try once more.");
    } finally {
      setGateSubmitting(false);
    }
  };

  const runTriage = async () => {
    if (!address.trim()) return;
    setLoading(true);
    const params = new URLSearchParams({
      address, unit_config: unitConfig,
      market_rent: String(marketRent), portfolio_size: String(portfolio),
    });
    if (attendeeId && attendeeId !== "bypass") params.set("attendee_id", attendeeId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/fwaa/triage?${params.toString()}`);
      if (r.ok) setTriage(await r.json());
    } catch { setTriage(null); }
    finally { setLoading(false); }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const shareLink = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams({ address, unit_config: unitConfig, market_rent: String(marketRent), portfolio_size: String(portfolio) });
    return `${window.location.origin}/fwaa-2026?${p.toString()}`;
  }, [address, unitConfig, marketRent, portfolio]);

  return (
    <div data-testid="fwaa-root" style={{
      minHeight: "100vh", background: BG, color: WHITE, fontFamily: SANS,
      backgroundImage: `
        radial-gradient(1200px 700px at 15% 5%, ${CYAN}18 0%, transparent 62%),
        radial-gradient(900px 620px at 88% 90%, ${CYAN}12 0%, transparent 60%),
        linear-gradient(180deg, ${BG} 0%, #030F2A 100%)
      `,
    }}>
      <style>{`
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 12px ${CYAN}44, inset 0 0 0 1px ${CYAN}66; }
          50%      { box-shadow: 0 0 22px ${CYAN}88, inset 0 0 0 1px ${CYAN}bb; }
        }
        @keyframes reveal { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .cy-input:focus  { outline: none; border-color: ${CYAN}; box-shadow: 0 0 12px ${CYAN}66; }
        .cy-btn:hover    { animation: glow 1.4s ease-in-out infinite; }
        .cy-card { animation: reveal 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        .cy-scanlines { background-image: repeating-linear-gradient(180deg, transparent 0, transparent 2px, ${CYAN}05 2px, ${CYAN}05 4px); }
      `}</style>

      {/* Top bar */}
      <div style={{ padding: "1.4rem 2rem", borderBottom: `1px solid ${SILVER}44`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <Link to="/" style={{ color: SILVER, textDecoration: "none", fontFamily: MONO, fontSize: "0.78rem", letterSpacing: "0.14em" }} data-testid="fwaa-back">
          &lt;/ MOOVIN.FORWARD
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          {scanCount != null && (
            <div style={{ fontFamily: MONO, fontSize: "0.72rem", color: CYAN, letterSpacing: "0.14em" }} data-testid="fwaa-scan-counter">
              LIVE_SCANS: <span style={{ fontWeight: 700 }}>{String(scanCount).padStart(4, "0")}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: CYAN, boxShadow: `0 0 12px ${CYAN}` }} />
            <span style={{ fontFamily: MONO, fontSize: "0.72rem", color: WHITE, letterSpacing: "0.16em" }}>SYSTEM ONLINE</span>
          </div>
        </div>
      </div>

      {/* ─── IDENTITY GATE (Step 1 — booth intake) ─── */}
      {!nextRoute && (
        <section style={{ padding: "2.4rem 1.25rem 2rem", maxWidth: 820, margin: "0 auto", width: "100%" }} className="cy-card" data-testid="fwa-gate">
          <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.72rem", letterSpacing: "0.28em", marginBottom: 12 }}>
            &gt; FWA_EXPO_ENTRY // 2026-08-06 // HURST_CONFERENCE_CENTER
          </div>
          <h1 style={{
            fontSize: "clamp(2rem, 5.6vw, 3.4rem)", fontWeight: 800, margin: "0 0 0.6rem",
            letterSpacing: "-0.02em", lineHeight: 1.05, color: WHITE,
          }} data-testid="fwa-gate-h1">
            Welcome. <span style={{ color: CYAN }}>Which seat do you hold?</span>
          </h1>
          <p style={{ color: SILVER, fontSize: "0.95rem", lineHeight: 1.6, maxWidth: 620, margin: "0 0 1.4rem" }}>
            The firm operates institutional interior stabilization for DFW multifamily
            portfolios. Tell us who you are and we route you to the correct desk — asset
            triage, capital, trade, or compliance.
          </p>

          {/* Affiliation buttons */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginBottom: 20 }} data-testid="fwa-gate-affiliations">
            {AFFILIATIONS.map((a) => {
              const active = affiliation === a.key;
              return (
                <button key={a.key} type="button" onClick={() => setAffiliation(a.key)}
                  data-testid={`fwa-affil-${a.key}`}
                  style={{
                    textAlign: "left", cursor: "pointer",
                    background: active ? `${CYAN}18` : "rgba(2,10,31,0.7)",
                    border: `1px solid ${active ? CYAN : SILVER + "44"}`,
                    color: active ? CYAN : WHITE,
                    borderRadius: 6, padding: "0.75rem 0.9rem",
                    boxShadow: active ? `0 0 16px ${CYAN}55` : "none",
                    transition: "border-color 0.15s, box-shadow 0.2s, background 0.15s",
                  }}>
                  <div style={{ fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.14em", color: active ? CYAN : SILVER, marginBottom: 4 }}>
                    {active ? "SELECTED" : "SELECT"}
                  </div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 700, lineHeight: 1.2, marginBottom: 3 }}>{a.label}</div>
                  <div style={{ fontSize: "0.72rem", color: SILVER, lineHeight: 1.35 }}>{a.hint}</div>
                </button>
              );
            })}
          </div>

          {/* Attendee fields */}
          <div style={{
            background: "rgba(2,10,31,0.7)", border: `1px solid ${SILVER}55`,
            borderRadius: 6, padding: "1.2rem 1.2rem", backdropFilter: "blur(8px)",
          }} className="cy-scanlines">
            <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 12 }}>
              &gt; ATTENDEE_DETAILS
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <FwaaField label="FULL_NAME *">
                <input data-testid="fwa-gate-name" className="cy-input" value={fullName}
                  onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" style={inputStyle} />
              </FwaaField>
              <FwaaField label="COMPANY">
                <input data-testid="fwa-gate-company" className="cy-input" value={company}
                  onChange={(e) => setCompany(e.target.value)} placeholder="Firm / portfolio / trade" style={inputStyle} />
              </FwaaField>
              <FwaaField label="JOB_TITLE">
                <input data-testid="fwa-gate-title" className="cy-input" value={jobTitle}
                  list="fwa-job-title-suggestions"
                  onChange={(e) => setJobTitle(e.target.value)} placeholder="Asset Manager · 1099 Worker · Cleaner…" style={inputStyle} />
                <datalist id="fwa-job-title-suggestions">
                  {JOB_TITLE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
                </datalist>
              </FwaaField>
              <FwaaField label="EMAIL">
                <input data-testid="fwa-gate-email" className="cy-input" value={email} type="email"
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@firm.com" style={inputStyle} />
              </FwaaField>
              <FwaaField label="PHONE">
                <input data-testid="fwa-gate-phone" className="cy-input" value={phone}
                  onChange={(e) => setPhone(e.target.value)} placeholder="(817) 555-0000" style={inputStyle} />
              </FwaaField>
              <FwaaField label="NOTES">
                <input data-testid="fwa-gate-notes" className="cy-input" value={notes}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Interest / follow-up preference" style={inputStyle} />
              </FwaaField>
            </div>
            {gateError && (
              <div style={{ color: "#ff8a8a", fontFamily: MONO, fontSize: "0.72rem", marginTop: 4, marginBottom: 8, letterSpacing: "0.08em" }} data-testid="fwa-gate-error">
                {gateError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button type="button" onClick={submitIdentity}
                disabled={gateSubmitting}
                data-testid="fwa-gate-continue"
                className="cy-btn"
                style={{
                  background: CYAN, color: BG, border: "none", padding: "0.85rem 1.6rem",
                  fontFamily: MONO, fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em",
                  cursor: gateSubmitting ? "wait" : "pointer", borderRadius: 4,
                }}>
                {gateSubmitting ? "SAVING…" : "CONTINUE >"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ─── Adaptive next screen based on affiliation ─── */}
      {nextRoute && nextRoute !== "triage" && (
        <FwaaRoleGate
          route={nextRoute}
          affiliationLabel={affiliationLabel}
          fullName={fullName}
          onSwitchSeat={() => { setNextRoute(""); setAttendeeId(""); }}
        />
      )}

      {/* HERO — triage flow (owner-side seats) */}
      {nextRoute === "triage" && (
      <>
      <section style={{ padding: "1.6rem 1.25rem 1rem", maxWidth: 1280, margin: "0 auto" }} className="cy-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }} data-testid="fwa-seat-badge">
          <span style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.18em", background: `${CYAN}12`, border: `1px solid ${CYAN}55`, borderRadius: 3, padding: "0.25rem 0.55rem" }}>
            SEAT · {affiliationLabel || "OWNER"}
          </span>
          {fullName && (
            <span style={{ color: SILVER, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.14em" }}>
              // {fullName}{company ? ` · ${company}` : ""}
            </span>
          )}
          <button type="button" onClick={() => { setNextRoute(""); setAttendeeId(""); setTriage(null); }}
            data-testid="fwa-switch-seat"
            style={{
              marginLeft: "auto", background: "transparent", color: SILVER,
              border: `1px solid ${SILVER}55`, borderRadius: 3, padding: "0.3rem 0.7rem",
              fontFamily: MONO, fontSize: "0.64rem", letterSpacing: "0.14em", cursor: "pointer",
            }}>
            SWITCH_SEAT
          </button>
        </div>
      </section>
      <section style={{ padding: "1.4rem 1.25rem 1.5rem", maxWidth: 1280, margin: "0 auto" }} className="cy-card">
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.72rem", letterSpacing: "0.28em", marginBottom: 12 }}>
          &gt; MOOVIN_FORWARD // INSTITUTIONAL_INTERIOR_STABILIZATION // FWA_EXPO // 2026-08-06
        </div>
        <h1 data-testid="fwaa-hero" style={{
          fontSize: "clamp(2.4rem, 6vw, 4.6rem)", fontWeight: 800, margin: "0 0 1rem",
          letterSpacing: "-0.02em", lineHeight: 1.02,
        }}>
          Portfolio-scale interior stabilization,<br />
          <span style={{ color: CYAN }}>quantified on scan.</span>
        </h1>
        <p style={{ fontFamily: SANS, fontSize: "1.05rem", color: SILVER, lineHeight: 1.65, maxWidth: 780, margin: "0 0 1rem" }}>
          Enter one address. The Moovin Forward stabilization engine executes a live triage against the
          identified asset, applies the <span style={{ color: CYAN, fontFamily: MONO }}>(Market Rent × 3) + 20%</span>
          stabilized rent target, generates a deterministic variation code, and returns real-time CapEx
          differential from unbundled procurement routing benchmarked against corporate general
          contractor pricing. Full compliance and capability disclosure upon engagement.
        </p>
      </section>

      {/* INPUT CARD */}
      <section style={{ padding: "0 2rem 2rem", maxWidth: 1280, margin: "0 auto" }} className="cy-card">
        <div style={{
          background: "rgba(2,10,31,0.7)", border: `1px solid ${SILVER}55`,
          borderRadius: 6, padding: "1.5rem 1.6rem", backdropFilter: "blur(8px)",
        }} className="cy-scanlines">
          <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 12 }}>
            &gt; ENTER_ASSET_PARAMETERS
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
            <FwaaField label="ASSET_ADDRESS">
              <input data-testid="fwaa-address" className="cy-input" value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="1234 W Main St, Fort Worth"
                style={inputStyle} />
            </FwaaField>
            <FwaaField label="UNIT_CONFIG">
              <select data-testid="fwaa-unit-config" className="cy-input" value={unitConfig}
                onChange={(e) => setUnitConfig(e.target.value)} style={inputStyle}>
                <option value="studio_1ba">Studio / 1 bath</option>
                <option value="1br_1ba">1 bed / 1 bath</option>
                <option value="2br_1ba">2 bed / 1 bath</option>
                <option value="2br_2ba">2 bed / 2 bath</option>
                <option value="3br_2ba">3 bed / 2 bath</option>
                <option value="3br_2_5ba">3 bed / 2.5 bath</option>
                <option value="4br_2ba">4 bed / 2 bath</option>
              </select>
            </FwaaField>
            <FwaaField label="MARKET_RENT / MO">
              <input data-testid="fwaa-market-rent" type="number" className="cy-input" value={marketRent}
                onChange={(e) => setMarketRent(Math.max(0, Number(e.target.value) || 0))}
                min="0" step="25" style={inputStyle} />
            </FwaaField>
            <FwaaField label="PORTFOLIO_SIZE">
              <input data-testid="fwaa-portfolio" type="number" className="cy-input" value={portfolio}
                onChange={(e) => setPortfolio(Math.max(1, Number(e.target.value) || 1))}
                min="1" step="1" style={inputStyle} />
            </FwaaField>
            <button type="button" data-testid="fwaa-run" onClick={runTriage} disabled={loading || !address.trim()}
              className="cy-btn" style={{
                background: CYAN, color: BG, border: "none", padding: "0.85rem 1.4rem",
                fontFamily: MONO, fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.12em",
                cursor: loading ? "wait" : "pointer", borderRadius: 4,
                opacity: !address.trim() ? 0.4 : 1,
              }}>
              {loading ? "SCANNING…" : "RUN_TRIAGE >"}
            </button>
          </div>
        </div>
      </section>

      {/* RESULT — SPLIT SCREEN */}
      {triage && (
        <section style={{ padding: "0 2rem 3rem", maxWidth: 1280, margin: "0 auto" }} className="cy-card" data-testid="fwaa-result">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* LEFT — Variation preview + codes */}
            <div style={{
              background: `linear-gradient(160deg, ${triage.variation.accent_hex}30, ${triage.variation.brass_hex}18)`,
              border: `1px solid ${SILVER}55`, borderRadius: 6, padding: "1.6rem 1.6rem",
              position: "relative", overflow: "hidden", minHeight: 460,
            }}>
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 8 }}>
                &gt; VARIATION_CODES
              </div>
              <div data-testid="fwaa-codes" style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                {["kitchen", "bath", "paint"].map((zone) => (
                  <div key={zone} style={{
                    background: `${CYAN}12`, border: `1px solid ${CYAN}55`, borderRadius: 4,
                    padding: "0.6rem 0.9rem", fontFamily: MONO, fontSize: "1.1rem",
                    fontWeight: 700, color: CYAN, letterSpacing: "0.06em",
                  }}>{triage.variation.codes[zone]}</div>
                ))}
              </div>

              <h2 style={{ fontSize: "1.55rem", fontWeight: 700, margin: "0 0 8px", lineHeight: 1.2, color: WHITE }}>
                {triage.variation.narrative.split(".")[0]}.
              </h2>
              <div style={{ color: SILVER, fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 20 }}>
                {triage.variation.narrative.split(".").slice(1).join(".").trim()}
              </div>

              {/* Dimension chips */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                {["jewel_tone_label", "hardware_label", "wood_label", "texture_label"].map((k) => (
                  <div key={k} data-testid={`fwaa-dim-${k}`} style={{
                    background: "rgba(2,10,31,0.7)", border: `1px solid ${SILVER}44`,
                    borderRadius: 4, padding: "0.55rem 0.75rem",
                  }}>
                    <div style={{ fontFamily: MONO, fontSize: "0.6rem", color: CYAN, letterSpacing: "0.14em" }}>{k.replace(/_label$/, "").toUpperCase()}</div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, marginTop: 3, color: WHITE }}>{triage.variation.dimensions[k]}</div>
                  </div>
                ))}
              </div>

              {/* Swatches */}
              <div style={{ display: "flex", gap: 8 }} data-testid="fwaa-swatches">
                <div style={{ width: 40, height: 60, background: triage.variation.accent_hex, borderRadius: 3, border: `1px solid ${SILVER}55` }} />
                <div style={{ width: 40, height: 60, background: triage.variation.brass_hex, borderRadius: 3, border: `1px solid ${SILVER}55` }} />
                <div style={{ width: 40, height: 60, background: "#F5EFE1", borderRadius: 3, border: `1px solid ${SILVER}55` }} />
                <div style={{ width: 40, height: 60, background: "#1a2537", borderRadius: 3, border: `1px solid ${SILVER}55` }} />
              </div>
            </div>

            {/* RIGHT — Unbundled procurement ledger + CapEx */}
            <div style={{
              background: "rgba(2,10,31,0.85)", border: `1px solid ${SILVER}55`,
              borderRadius: 6, padding: "1.6rem 1.6rem", minHeight: 460,
            }}>
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 8 }}>
                &gt; UNBUNDLED_PROCUREMENT_LEDGER
              </div>
              <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: "0 0 6px", color: WHITE }}>
                CapEx Savings <span style={{ color: CYAN, fontFamily: MONO }}>{triage.capex.savings_pct}%</span>
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <StatBox label="UNBUNDLED" value={fmt(triage.capex.unbundled_total)} color={CYAN} testid="fwaa-unbundled-total" />
                <StatBox label="CORP.GC" value={fmt(triage.capex.corporate_total)} color={SILVER} strike testid="fwaa-corporate-total" />
              </div>
              <div style={{
                background: `${CYAN}12`, border: `1px solid ${CYAN}66`, borderRadius: 4,
                padding: "0.6rem 0.85rem", marginBottom: 14,
              }}>
                <div style={{ fontFamily: MONO, fontSize: "0.62rem", color: CYAN, letterSpacing: "0.16em" }}>PER_UNIT_SAVINGS</div>
                <div style={{ fontSize: "1.6rem", fontWeight: 800, color: CYAN, fontFamily: MONO }} data-testid="fwaa-per-unit-savings">
                  {fmt(triage.capex.total_capex_savings)}
                </div>
              </div>

              {/* Ledger table */}
              <div style={{ maxHeight: 240, overflowY: "auto", border: `1px solid ${SILVER}22`, borderRadius: 4 }} data-testid="fwaa-ledger">
                {triage.unbundled_ledger.map((row, i) => (
                  <div key={row.role} style={{
                    display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr",
                    padding: "0.5rem 0.7rem", borderBottom: `1px solid ${SILVER}18`,
                    fontSize: "0.72rem", alignItems: "center",
                    background: i % 2 === 0 ? "transparent" : "rgba(0,255,255,0.02)",
                  }}>
                    <div>
                      <div style={{ color: WHITE, fontWeight: 600 }}>{row.role_label}</div>
                      <div style={{ color: MUTED, fontSize: "0.62rem", marginTop: 1 }}>{row.system_label}</div>
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: "0.62rem", color: SILVER, textAlign: "right" }}>
                      <span style={{ color: MUTED, textDecoration: "line-through" }}>{fmt(row.corporate_cost)}</span>
                      <span style={{ color: CYAN, marginLeft: 6 }}>{fmt(row.unbundled_cost)}</span>
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: "0.6rem", color: CYAN, textAlign: "right", letterSpacing: "0.06em" }}>
                      {row.supplier_tag.split("_")[0]}<br/>
                      <span style={{ color: MUTED }}>{row.shipping_window_hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* STABILIZATION MATH + PORTFOLIO PROJECTION */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            {/* Stabilization Math */}
            <div style={{ background: "rgba(2,10,31,0.85)", border: `1px solid ${CYAN}55`, borderRadius: 6, padding: "1.4rem 1.6rem" }} data-testid="fwaa-stabilization-math">
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 8 }}>
                &gt; STABILIZATION_MATH
              </div>
              <div style={{ fontFamily: MONO, fontSize: "1.9rem", fontWeight: 700, color: CYAN, marginBottom: 4 }} data-testid="fwaa-formula">
                (Market Rent × 3) + 20%
              </div>
              <div style={{ color: SILVER, fontSize: "0.85rem", marginBottom: 14 }}>
                (Market Rent × 3) + 20% = stabilized rent target.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <StatBox label="MARKET" value={fmt(triage.stabilization_math.market_rent)} color={WHITE} testid="fwaa-market" />
                <StatBox label="TARGET" value={fmt(triage.stabilization_math.stabilized_rent_target)} color={CYAN} testid="fwaa-stabilized" />
                <StatBox label="FEE / MO" value={fmt(triage.stabilization_math.service_fee_monthly)} color={CYAN} testid="fwaa-fee-monthly" />
              </div>
            </div>

            {/* Portfolio Projection */}
            <div style={{ background: `${CYAN}0d`, border: `1px solid ${CYAN}66`, borderRadius: 6, padding: "1.4rem 1.6rem" }} data-testid="fwaa-portfolio-projection">
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 8 }}>
                &gt; PORTFOLIO_PROJECTION · {triage.portfolio_projection.units_in_portfolio} UNITS
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: SILVER, fontSize: "0.7rem", letterSpacing: "0.14em", fontFamily: MONO }}>PROJECTED CAPEX SAVED</div>
                <div style={{ fontSize: "2.4rem", fontWeight: 800, color: CYAN, fontFamily: MONO, letterSpacing: "-0.02em" }} data-testid="fwaa-portfolio-savings">
                  {fmt(triage.portfolio_projection.projected_capex_savings)}
                </div>
              </div>
              <div>
                <div style={{ color: SILVER, fontSize: "0.7rem", letterSpacing: "0.14em", fontFamily: MONO }}>PROJECTED ANNUAL FEE REVENUE</div>
                <div style={{ fontSize: "1.4rem", fontWeight: 700, color: WHITE, fontFamily: MONO }} data-testid="fwaa-portfolio-annual-fee">
                  {fmt(triage.portfolio_projection.projected_annual_fee_revenue)}
                </div>
              </div>
            </div>
          </div>

          {/* Asset protection callout */}
          <div style={{ marginTop: 16, background: `${CYAN}0a`, border: `1px solid ${SILVER}44`, borderRadius: 6, padding: "1.2rem 1.4rem" }} data-testid="fwaa-asset-protection">
            <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.68rem", letterSpacing: "0.22em", marginBottom: 6 }}>
              &gt; HARDWIRED_ASSET_PROTECTION · {triage.asset_protection.sku}
            </div>
            <div style={{ fontSize: "1rem", fontWeight: 600, color: WHITE, marginBottom: 4 }}>
              {triage.asset_protection.label}
            </div>
            <div style={{ color: SILVER, fontSize: "0.85rem", lineHeight: 1.55 }}>
              {triage.asset_protection.operational_intent} <span style={{ color: CYAN }}>Ownership transfers permanently to your portfolio.</span>
            </div>
          </div>

          {/* Share + CTA */}
          <div style={{ marginTop: 20, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button type="button" data-testid="fwaa-share" onClick={() => { try { navigator.clipboard.writeText(shareLink); } catch (e) { void e; } }}
              className="cy-btn"
              style={{
                background: "transparent", color: CYAN, border: `1px solid ${CYAN}`,
                padding: "0.85rem 1.6rem", borderRadius: 4, fontFamily: MONO,
                fontSize: "0.76rem", fontWeight: 700, letterSpacing: "0.14em", cursor: "pointer",
              }}>
              // COPY_SHAREABLE_LINK
            </button>
            <Link to="/#contact" data-testid="fwaa-book"
              className="cy-btn"
              style={{
                background: CYAN, color: BG, border: "none",
                padding: "0.85rem 2rem", borderRadius: 4, fontFamily: MONO,
                fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em",
                textDecoration: "none",
              }}>
              REQUEST_WALKTHROUGH &gt;
            </Link>
          </div>
        </section>
      )}
      </>
      )}

      {/* Footer */}
      <footer style={{ padding: "2rem", textAlign: "center", borderTop: `1px solid ${SILVER}22`, marginTop: "auto" }}>
        <div style={{ fontFamily: MONO, fontSize: "0.66rem", color: MUTED, letterSpacing: "0.18em" }}>
          MOOVIN FORWARD LLC · INSTITUTIONAL INTERIOR STABILIZATION · DFW METROPLEX · BLUEPRINT_v2.5.0
        </div>
      </footer>

      {/* Booth install prompt — pins the PWA to purse holder home screens */}
      <InstallPrompt />
    </div>
  );
};

const FwaaField = ({ label, children }) => (
  <div>
    <div style={{ fontFamily: MONO, fontSize: "0.6rem", color: CYAN, letterSpacing: "0.16em", marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const inputStyle = {
  width: "100%", background: "rgba(2,10,31,0.7)", color: WHITE,
  border: `1px solid ${SILVER}55`, borderRadius: 3,
  padding: "0.65rem 0.8rem", fontFamily: MONO, fontSize: "0.85rem",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

const StatBox = ({ label, value, color, strike, testid }) => (
  <div data-testid={testid} style={{
    background: "rgba(2,10,31,0.7)", border: `1px solid ${color}44`,
    borderRadius: 4, padding: "0.55rem 0.75rem", textAlign: "center",
  }}>
    <div style={{ fontFamily: MONO, fontSize: "0.6rem", color: color, letterSpacing: "0.16em" }}>{label}</div>
    <div style={{ fontFamily: MONO, fontSize: "1.05rem", fontWeight: 700, color: strike ? MUTED : color, textDecoration: strike ? "line-through" : "none", marginTop: 3 }}>
      {value}
    </div>
  </div>
);

// ─── Adaptive next-screen for non-triage seats ─────────────────
// firm_intro is a shared one-liner every non-triage seat sees at the
// top of their gate. Keeps public copy institutional, no sales talk.
const FIRM_INTRO =
  "Moovin Forward LLC is a DFW-based institutional interior stabilization firm. We turn vacant multifamily units back into leaseable, revenue-generating inventory for portfolio owners.";

const ROLE_CONTENT = {
  apply: {
    testid: "fwa-role-apply",
    eyebrow: "TRADE_DESK · CONTRACTOR_ONBOARDING",
    h1: "You're a trade. Let's get you on file.",
    body:
      FIRM_INTRO + " Independent contractors, 1099 workers, and small crews are how we execute in the field — every scope you see below flows through vetted trades in your ZIP.",
    sections: [
      {
        heading: "WHAT_WORK_YOU_LL_GET",
        items: [
          "Interior turn cleaning and deep sanitation",
          "Punch-out, drywall patch, and paint touch-up",
          "Cabinet refacing, hardware modernization, countertop work",
          "Flooring (LVP, tile, carpet) install and repair",
          "HVAC service, electrical, plumbing scopes",
          "ADA / Section 504 accessibility conversions",
        ],
      },
      {
        heading: "HOW_YOU_GET_MATCHED",
        items: [
          "Fill out the vendor application (trade, ZIP, license, insurance)",
          "You are auto-scored on tenure, coverage area, and community-first status",
          "When a job matches your trade and ZIP, you get an SMS via our dispatcher",
          "Accept the job, execute the scope, upload before/after photos",
        ],
      },
      {
        heading: "HOW_YOU_GET_PAID",
        items: [
          "You work 1099 — direct crew paid by Moovin Forward, or referral to the LP direct",
          "Per-scope pricing locked at assignment, no bait-and-switch",
          "Payment triggered on scope completion + photo verification",
          "Community-first vendors (small operators) get priority routing",
        ],
      },
    ],
    ctaLabel: "OPEN_APPLICATION >",
    ctaTo: "/apply",
  },
  ada_walkthrough: {
    testid: "fwa-role-ada",
    eyebrow: "COMPLIANCE_DESK · ADA / SECTION_504",
    h1: "Accessibility is a first-class stabilization category.",
    body:
      FIRM_INTRO + " ADA and Section 504 scopes are handled inside the interior stabilization workflow — not bolted on afterward. Reach out to review case-specific unit conversions, documentation, and inspection pathways.",
    ctaLabel: "REQUEST_WALKTHROUGH >",
    ctaTo: "/#contact",
  },
  press_packet: {
    testid: "fwa-role-media",
    eyebrow: "PRESS_DESK · MEDIA_INQUIRIES",
    h1: "Thanks for stopping by. Coverage requests welcome.",
    body:
      FIRM_INTRO + " Editorial, photography, and interview requests are handled by direct outreach — we do not distribute a public press kit at the booth. Leave your details above and we will follow up with a curated media briefing.",
    ctaLabel: "OPEN_CONTACT >",
    ctaTo: "/#contact",
  },
  sponsor_packet: {
    testid: "fwa-role-sponsor",
    eyebrow: "PARTNERSHIP_DESK · SPONSOR_INTAKE",
    h1: "Sponsor and partner conversations are 1:1.",
    body:
      FIRM_INTRO + " Sponsorship, referral, and joint-venture discussions are handled privately. Your details are captured — a partnership brief will be sent to your email within one business day.",
    ctaLabel: "REQUEST_BRIEF >",
    ctaTo: "/#contact",
  },
  walkthrough_request: {
    testid: "fwa-role-walk",
    eyebrow: "GENERAL_DESK · WALKTHROUGH_REQUEST",
    h1: "Thanks — we'll route the right desk to you.",
    body:
      FIRM_INTRO + " Your details have been captured. If you want to be walked through the firm's stabilization workflow, request a walk-through and we'll book time.",
    ctaLabel: "REQUEST_WALKTHROUGH >",
    ctaTo: "/#contact",
  },
};

const FwaaRoleGate = ({ route, affiliationLabel, fullName, onSwitchSeat }) => {
  const c = ROLE_CONTENT[route] || ROLE_CONTENT.walkthrough_request;
  return (
    <section data-testid={c.testid} style={{ padding: "2rem 1.25rem 3rem", maxWidth: 820, margin: "0 auto", width: "100%" }} className="cy-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.18em", background: `${CYAN}12`, border: `1px solid ${CYAN}55`, borderRadius: 3, padding: "0.25rem 0.55rem" }}>
          SEAT · {affiliationLabel || "GUEST"}
        </span>
        {fullName && (
          <span style={{ color: SILVER, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.14em" }}>
            // {fullName}
          </span>
        )}
        <button type="button" onClick={onSwitchSeat}
          data-testid="fwa-role-switch-seat"
          style={{
            marginLeft: "auto", background: "transparent", color: SILVER,
            border: `1px solid ${SILVER}55`, borderRadius: 3, padding: "0.3rem 0.7rem",
            fontFamily: MONO, fontSize: "0.64rem", letterSpacing: "0.14em", cursor: "pointer",
          }}>
          SWITCH_SEAT
        </button>
      </div>
      <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.7rem", letterSpacing: "0.24em", marginBottom: 10 }}>
        &gt; {c.eyebrow}
      </div>
      <h1 style={{ fontSize: "clamp(1.7rem, 5vw, 2.6rem)", fontWeight: 800, margin: "0 0 0.8rem", lineHeight: 1.1, letterSpacing: "-0.02em", color: WHITE }}>
        {c.h1}
      </h1>
      <p style={{ color: SILVER, fontSize: "1rem", lineHeight: 1.65, maxWidth: 640, margin: "0 0 1.4rem" }}>
        {c.body}
      </p>

      {/* Optional info sections — only the vendor/trade route uses these today. */}
      {c.sections && (
        <div style={{ display: "grid", gap: 12, marginBottom: 20 }} data-testid="fwa-role-sections">
          {c.sections.map((s) => (
            <div key={s.heading} style={{
              background: "rgba(2,10,31,0.7)", border: `1px solid ${SILVER}44`,
              borderRadius: 6, padding: "0.95rem 1.05rem",
            }}>
              <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.22em", marginBottom: 8 }}>
                &gt; {s.heading}
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.15rem", color: WHITE, fontSize: "0.9rem", lineHeight: 1.6 }}>
                {s.items.map((it) => (
                  <li key={it} style={{ marginBottom: 4 }}>{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Link to={c.ctaTo} data-testid="fwa-role-cta" className="cy-btn"
          style={{
            background: CYAN, color: BG, border: "none",
            padding: "0.9rem 1.8rem", borderRadius: 4, fontFamily: MONO,
            fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.14em",
            textDecoration: "none",
          }}>
          {c.ctaLabel}
        </Link>
        <Link to="/" data-testid="fwa-role-home"
          style={{
            background: "transparent", color: SILVER,
            border: `1px solid ${SILVER}55`, padding: "0.9rem 1.4rem", borderRadius: 4,
            fontFamily: MONO, fontSize: "0.76rem", letterSpacing: "0.14em",
            textDecoration: "none",
          }}>
          VISIT_HOMEPAGE
        </Link>
      </div>
    </section>
  );
};

export default FWAA;
